/**
 * <s-country-switch> — Saves the shopper's country in the background so the page never reloads.
 *
 * Required child markup:
 *   form                          — Shopify localization form.
 *   button[name="country_code"]   — One per country, with data-flag (flag image URL) and data-name.
 * Optional child markup:
 *   button[data-international]    — Display-only "International" choice for countries not listed; remembered on this device.
 * Optional, anywhere in the document:
 *   [data-current-country-flag]   — <img> updated to the chosen flag.
 *   [data-current-country-globe]  — Globe icon shown instead of the flag when International is chosen.
 *   [data-current-country-name]   — Element whose text becomes the chosen country name.
 * Falls back to a normal form submit if the background save fails.
 *
 * Automatic country: until the shopper picks a country themselves, the visitor's real country
 * (window.anjaniGeo, set in the layout head) is selected; a country not in the list shows
 * International. A manual pick is remembered on this device and is never overridden.
 */
const STORAGE_KEY = 'anjani-country-choice';
const MANUAL_KEY = 'anjani-country-manual';
const INTERNATIONAL = 'international';

class SCountrySwitch extends HTMLElement {
  #form;
  #intlButton;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#intlButton = this.querySelector('[data-international]');
    this.#form?.addEventListener('submit', this.#onSubmit);
    this.#intlButton?.addEventListener('click', this.#onInternational);
    if (this.#intlButton && this.#readChoice() === INTERNATIONAL) this.#markInternational();
    this.#autoSelect();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#intlButton?.removeEventListener('click', this.#onInternational);
  }

  #readChoice() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  #writeChoice(value) {
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Storage unavailable (private mode); the choice simply isn't remembered. */
    }
  }

  #isManual() {
    try {
      // Older visits stored International only when it was picked by hand.
      return localStorage.getItem(MANUAL_KEY) === '1' || localStorage.getItem(STORAGE_KEY) === INTERNATIONAL;
    } catch {
      return true;
    }
  }

  #setManual() {
    try { localStorage.setItem(MANUAL_KEY, '1'); } catch { /* not remembered */ }
  }

  async #autoSelect() {
    if (this.#isManual() || typeof window.anjaniGeo !== 'function') return;
    const geo = await window.anjaniGeo();
    if (!geo || this.#isManual()) return;
    const match = [...this.querySelectorAll('button[name="country_code"]')].find((b) => b.value === geo.countryCode);
    if (!match) {
      if (this.#intlButton) {
        this.#writeChoice(INTERNATIONAL);
        this.#markInternational();
      }
      return;
    }
    this.#writeChoice(null);
    if (match.getAttribute('aria-current') === 'true') {
      this.#markCurrent(match);
      return;
    }
    this.#markCurrent(match);
    this.#save(match, false);
  }

  #onInternational = () => {
    this.#setManual();
    this.#writeChoice(INTERNATIONAL);
    this.#markInternational();
    this.closest('dialog')?.close();
  };

  #onSubmit = async (event) => {
    const button = event.submitter;
    if (!button || button.name !== 'country_code') return;
    event.preventDefault();
    this.#setManual();
    this.#writeChoice(null);
    this.#markCurrent(button);
    this.closest('dialog')?.close();
    this.#save(button, true);
  };

  async #save(button, fallbackSubmit) {
    const data = new FormData(this.#form);
    data.set('country_code', button.value);
    try {
      const response = await fetch(this.#form.action, { method: 'POST', body: data, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Country update failed: ${response.status}`);
    } catch {
      if (!fallbackSubmit) return; // automatic choice: just try again on the next page
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'country_code';
      input.value = button.value;
      this.#form.append(input);
      this.#form.submit();
    }
  }

  #setCurrent(selected) {
    for (const button of this.querySelectorAll('button[name="country_code"], [data-international]')) {
      if (button === selected) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  }

  #markCurrent(selected) {
    this.#setCurrent(selected);
    for (const img of document.querySelectorAll('[data-current-country-flag]')) {
      img.src = selected.dataset.flag;
      img.hidden = false;
    }
    for (const globe of document.querySelectorAll('[data-current-country-globe]')) {
      globe.classList.add('hidden');
      globe.classList.remove('inline-flex');
    }
    for (const el of document.querySelectorAll('[data-current-country-name]')) {
      el.textContent = selected.dataset.name;
    }
  }

  #markInternational() {
    this.#setCurrent(this.#intlButton);
    for (const img of document.querySelectorAll('[data-current-country-flag]')) {
      img.hidden = true;
    }
    for (const globe of document.querySelectorAll('[data-current-country-globe]')) {
      globe.classList.remove('hidden');
      globe.classList.add('inline-flex');
    }
    for (const el of document.querySelectorAll('[data-current-country-name]')) {
      el.textContent = this.#intlButton.dataset.name;
    }
  }
}

if (!customElements.get('s-country-switch')) {
  customElements.define('s-country-switch', SCountrySwitch);
}
